/**
 * Custom hook for property autocomplete
 * 
 * Provides debounced autocomplete suggestions for property names from Elasticsearch
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import type { APINamedAutocompleteResponse } from '../../../generated-types/models/APINamedAutocompleteResponse'
import type { V1SearchItemsPropertiesAutocompleteRetrieveRequest } from '../../../generated-types/apis/V1Api'

export interface PropertySuggestion {
  id: string
  name: string
  self: string
}

export interface PropertyAutocompleteResponse {
  suggestions: PropertySuggestion[]
  total: number
}

/**
 * Fetches property autocomplete suggestions
 */
const fetchPropertyAutocomplete = async (
  query: string,
  size = 10
): Promise<PropertyAutocompleteResponse> => {
  const response: APINamedAutocompleteResponse = await v1Api.v1SearchItemsPropertiesAutocompleteRetrieve({
    q: query || undefined,
    size,
  } as V1SearchItemsPropertiesAutocompleteRetrieveRequest)

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.name,
      self: `${baseUrl}v1/items/properties/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Hook for property name autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 * 
 * @example
 * ```tsx
 * const { suggestions, isLoading, updateQuery } = usePropertyAutocomplete()
 * 
 * <input onChange={(e) => updateQuery(e.target.value)} />
 * {suggestions.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
 * ```
 */
export function usePropertyAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<PropertyAutocompleteResponse, Error>({
    queryKey: ['property-autocomplete', debouncedQuery],
    queryFn: () => fetchPropertyAutocomplete(debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


